import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface SelectedFilial {
  id: string;
  codigo: string;
  nome_fantasia: string;
}

interface SelectedFilialContextValue {
  filial: SelectedFilial | null;
  setFilial: (filial: SelectedFilial | null) => void;
}

const STORAGE_KEY = "axis_selected_filial";

const SelectedFilialContext = createContext<SelectedFilialContextValue | undefined>(undefined);

function readStored(): SelectedFilial | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.id && parsed.codigo) {
      return {
        id: String(parsed.id),
        codigo: String(parsed.codigo),
        nome_fantasia: String(parsed.nome_fantasia ?? ""),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function SelectedFilialProvider({ children }: { children: ReactNode }) {
  const [filial, setFilialState] = useState<SelectedFilial | null>(() => readStored());

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (filial) localStorage.setItem(STORAGE_KEY, JSON.stringify(filial));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [filial]);

  const value = useMemo<SelectedFilialContextValue>(
    () => ({ filial, setFilial: setFilialState }),
    [filial],
  );

  return (
    <SelectedFilialContext.Provider value={value}>{children}</SelectedFilialContext.Provider>
  );
}

export function useSelectedFilial(): SelectedFilialContextValue {
  const ctx = useContext(SelectedFilialContext);
  if (!ctx) throw new Error("useSelectedFilial deve ser usado dentro de SelectedFilialProvider");
  return ctx;
}
